/**
 * Confetti Effect Component
 * 
 * Renders animated confetti particles for celebrations
 * in Emmy's Learning Adventure.
 */

import React, { useEffect, useState, useRef, memo } from 'react';
import PropTypes from 'prop-types';

// Default colors array - defined outside component to ensure stable reference
const DEFAULT_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

const ConfettiEffect = memo(({ 
  active = false, 
  duration = 3000, 
  particleCount = 50,
  colors = DEFAULT_COLORS,
  onComplete = () => {}
}) => {
  const [particles, setParticles] = useState([]);
  // Use refs to store props to avoid re-render loops from changing dependencies
  const onCompleteRef = useRef(onComplete);
  const colorsRef = useRef(colors);
  const particleCountRef = useRef(particleCount);
  const durationRef = useRef(duration);
  const activeRef = useRef(active);
  const hasGeneratedRef = useRef(false);
  
  // Update refs when props change
  useEffect(() => {
    onCompleteRef.current = onComplete;
    colorsRef.current = colors;
    particleCountRef.current = particleCount;
    durationRef.current = duration;
  }, [onComplete, colors, particleCount, duration]);

  useEffect(() => {
    const wasActive = activeRef.current;
    activeRef.current = active;

    if (!active) {
      if (wasActive) {
        // Only clear particles if we were just active
        setParticles([]);
        hasGeneratedRef.current = false;
      }
      return;
    }

    // If particles were already generated for this active session, don't regenerate
    if (hasGeneratedRef.current && wasActive) {
      return;
    }

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      onCompleteRef.current();
      hasGeneratedRef.current = true;
      return;
    }

    // Generate confetti particles only once when active becomes true
    const newParticles = Array.from({ length: particleCountRef.current }, (_, i) => ({
      id: i,
      color: colorsRef.current[Math.floor(Math.random() * colorsRef.current.length)],
      left: Math.random() * 100,
      animationDelay: Math.random() * 2,
      size: Math.random() * 6 + 4,
      shape: Math.random() > 0.5 ? 'circle' : 'square',
      rotation: Math.random() * 360
    }));

    setParticles(newParticles);
    hasGeneratedRef.current = true;

    // Clean up after duration
    const timer = setTimeout(() => {
      setParticles([]);
      hasGeneratedRef.current = false;
      onCompleteRef.current();
    }, durationRef.current);

    return () => clearTimeout(timer);
  }, [active]); // Only depend on active - use refs for other values

  if (!active || particles.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-50"
      style={{ overflow: 'hidden' }}
    >
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="confetti-particle absolute"
          style={{
            left: `${particle.left}%`,
            top: '-10px',
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            borderRadius: particle.shape === 'circle' ? '50%' : '0',
            animationDelay: `${particle.animationDelay}s`,
            transform: `rotate(${particle.rotation}deg)`
          }}
        />
      ))}
    </div>
  );
});

ConfettiEffect.propTypes = {
  active: PropTypes.bool,
  duration: PropTypes.number,
  particleCount: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
  onComplete: PropTypes.func
};

ConfettiEffect.defaultProps = {
  active: false,
  duration: 3000,
  particleCount: 50,
  colors: DEFAULT_COLORS,
  onComplete: () => {}
};

ConfettiEffect.displayName = 'ConfettiEffect';

export default ConfettiEffect;